import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DropdownComponent,
  DropdownOption,
} from '../../../shared/components/ui/dropdown/dropdown.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { ApiService } from '../../../core/services/api.service';
import {
  PaginatedTableComponent,
  TableHeader,
} from '../../../shared/components/tables/paginated-table/paginated-table.component';
import { AlertComponent } from '../../../shared/components/alert/alert.component';
import { IconComponent, IconName } from '../../../shared/icons/icon.component';
import { ToasterService } from '../../../core/services/toaster.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationModalComponent } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { LoaderService } from '../../../core/services/loader.service';
import { UserFormPanelComponent } from './components/user-form-panel/user-form-panel.component';
import { AuditLogPanelComponent } from '../../../shared/components/audit-log-panel/audit-log-panel.component';
import { AuditEntityType } from '../../../core/models/audit-log.model';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  avatarUrl?: string;
  roleId: string;
  role?: {
    id: string;
    name: string;
  };
  isTwoFAEnabled: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
  productAccessType?: string;
  productIds?: string[];
  UserProducts?: any[];
}

interface UserApiResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

@Component({
  selector: 'app-user',
  imports: [
    FormsModule,
    CommonModule,
    DropdownComponent,
    ButtonComponent,
    PaginatedTableComponent,
    AlertComponent,
    IconComponent,
    UserFormPanelComponent,
    AuditLogPanelComponent,
  ],
  standalone: true,
  templateUrl: './user.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .custom-scrollbar::-webkit-scrollbar {
        width: 5px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(139, 92, 246, 0.2);
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: rgba(139, 92, 246, 0.4);
      }
    `,
  ],
})
export class UserComponent implements OnInit {
  @ViewChild('dateDropdown') dateDropdown!: DropdownComponent;
  @ViewChild(UserFormPanelComponent) formPanel!: UserFormPanelComponent;
  @ViewChild(AuditLogPanelComponent) auditLogPanel!: AuditLogPanelComponent;

  loaderService = inject(LoaderService);
  private toasterService = inject(ToasterService);
  private dialog = inject(MatDialog);
  private apiservice = inject(ApiService);
  router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  // States
  isLoading = signal(false);
  errorMessage = signal('');
  successMessage = signal('');
  totalItems = signal(0);

  // Filters
  searchTerm = signal('');
  selectedRoleId = signal<string | null>(null);
  selectedStatus = signal<string | null>(null);
  page = signal(1);
  pageSize = signal(10);
  sortBy = signal('createdAt');
  sortOrder = signal('');
  userData = signal<User[]>([]);

  auditEntityTypes = [AuditEntityType.USER];

  // Table Headers
  tableHeaders: TableHeader[] = [
    { label: 'Name', key: 'fullName', align: 'text-left' },
    { label: 'Email', key: 'email', align: 'text-left' },
    { label: 'Phone', key: 'phoneNumber', align: 'text-left' },
    { label: 'Role', key: 'roleName', align: 'text-left' },
    { label: 'Status', key: 'status', align: 'text-left' },
    {
      label: 'Joining Date',
      key: 'createdAt',
      align: 'text-left',
      dateFormat: 'dashDate',
    },
  ];

  tableActionOptions = [
    {
      label: 'Edit',
      value: 'edit',
      iconName: 'squarePen' as IconName,
      isShow: true,
      visibilityKey: 'isNotDeleted',
      color: '#1E6AFD',
    },
    {
      label: 'Delete',
      value: 'delete',
      iconName: 'trash' as IconName,
      isShow: true,
      visibilityKey: 'isNotDeleted',
      color: 'red',
    },
    {
      label: 'Restore',
      value: 'restore',
      iconName: 'rotateCcw' as IconName,
      isShow: true,
      visibilityKey: 'isDeleted',
      color: '#AD46FF',
    },
  ];

  // User Dropdown Options
  roleOptions: DropdownOption[] = [];

  statusOptions: DropdownOption[] = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'SUSPENDED', label: 'Suspended' },
    { value: 'DELETED', label: 'Deleted' },
  ];

  get processedUserData() {
    return this.userData().map((user) => ({
      ...user,
      fullName: `${user.firstName} ${user.lastName}`,
      roleName: user.role?.name || 'N/A',
      isDeleted: user.status === 'DELETED',
      isNotDeleted: user.status !== 'DELETED',
    }));
  }

  ngOnInit() {
    this.fetchRoles();
    this.fetchUsers();
  }

  fetchRoles() {
    this.apiservice.get('/roles/list').subscribe({
      next: (res: any) => {
        let rolesData = [];
        if (res?.roles && Array.isArray(res.roles)) {
          rolesData = res.roles;
        } else if (Array.isArray(res)) {
          rolesData = res;
        } else if (res?.data && Array.isArray(res.data)) {
          rolesData = res.data;
        }

        if (rolesData.length > 0) {
          this.roleOptions = rolesData.map((role: any) => ({
            value: role.id,
            label: role.name || role.displayName || role.label,
          }));
          this.cdr.markForCheck();
        }
      },
      error: (err) => {
        this.toasterService.error('Failed to fetch roles');
        console.error('Role fetch error:', err);
      },
    });
  }

  fetchUsers(): void {
    this.isLoading.set(true);
    const params: any = {
      page: this.page(),
      limit: this.pageSize(),
      sortBy: this.sortBy(),
    };

    const status = this.selectedStatus();
    const roleId = this.selectedRoleId();
    const search = this.searchTerm();
    const sortOrder = this.sortOrder();

    if (status) params.status = status;
    if (roleId) params.roleId = roleId;
    if (search) params.search = search;
    if (sortOrder) params.sortOrder = sortOrder;

    this.apiservice
      .get('/user', params, {
        showLoader: true,
        showErrorToaster: true,
        showSuccessToaster: false,
      })
      .subscribe({
        next: (response: any) => {
          const apiResponse = response as UserApiResponse;
          this.userData.set(apiResponse.users || []);
          this.totalItems.set(apiResponse.pagination?.total || 0);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          this.isLoading.set(false);
          this.toasterService.error(
            error?.error || error?.message || 'Failed to load user list',
            'User List',
            2000,
          );
        },
      });
  }

  // Filter Handlers
  onRoleFilter(roleId: string | string[] | null): void {
    const selectedId = Array.isArray(roleId) ? roleId[0] : roleId;
    this.selectedRoleId.set(selectedId === '' ? null : selectedId);
  }

  onStatusFilter(status: string | string[] | null): void {
    const selectedId = Array.isArray(status) ? status[0] : status;
    this.selectedStatus.set(selectedId === '' ? null : selectedId);
  }

  onSearch(term: string): void {
    this.searchTerm.set(term);
  }

  applyFilters() {
    this.page.set(1);
    this.fetchUsers();
  }

  onPageChange(page: number): void {
    this.page.set(page);
    this.fetchUsers();
  }

  clearFilters() {
    this.selectedRoleId.set(null);
    this.selectedStatus.set(null);
    this.searchTerm.set('');
    this.page.set(1);
    this.fetchUsers();
  }

  // Event Handlers for Table
  selectedRowItem(item: any) {
    if (!item || !item.row) return;
    if (item.action === 'edit') {
      this.openPanel(item.row);
    } else if (item.action === 'delete') {
      this.openConfirmationDialog(item.row);
    } else if (item.action === 'restore') {
      this.handleRestoreUser(item.row.id);
    }
  }

  handleUserSaved() {
    this.fetchUsers();
    this.auditLogPanel?.reloadLogs();
  }

  openConfirmationDialog(row: any) {
    this.dialog
      .open(ConfirmationModalComponent, {
        width: '400px',
        data: {
          title: 'Confirm Deletion',
          description: `Are you sure you want to delete this user?`,
        },
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) this.handleRemoveUser(row.id);
      });
  }

  handleRemoveUser(id: string) {
    this.apiservice
      .delete(`/user/${id}`, {
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: true,
      })
      .subscribe({
        next: () => {
          this.toasterService.success('User removed successfully', 'Success');
          this.fetchUsers();
          this.auditLogPanel?.reloadLogs();
        },
        error: (error) =>
          this.errorMessage.set(
            error?.error || error?.message || 'Error removing user',
          ),
      });
  }

  handleRestoreUser(id: string) {
    this.apiservice
      .post(
        `/user/${id}/restore`,
        {},
        {
          showErrorToaster: true,
          showSuccessToaster: false,
          showLoader: true,
        },
      )
      .subscribe({
        next: () => {
          this.toasterService.success('User restored successfully', 'Success');
          this.fetchUsers();
          this.auditLogPanel?.reloadLogs();
        },
        error: (error) =>
          this.errorMessage.set(
            error?.error || error?.message || 'Error restoring user',
          ),
      });
  }

  openPanel(user?: any) {
    this.formPanel.openPanel(user);
  }

  openAddUserPanel() {
    this.openPanel();
  }
}
